/**
 * URL Utility Functions
 * Shared utilities for normalizing and validating URLs across the application
 */

/**
 * Normalizes a URL by ensuring it has a protocol (http:// or https://)
 * Also detects email addresses and converts them to mailto: links
 * @param {string} url - The URL or email to normalize
 * @returns {string} - The normalized URL with protocol or mailto: link
 *
 * Examples:
 * - "example.com" → "https://example.com"
 * - "www.example.com" → "https://www.example.com"
 * - "https://example.com" → "https://example.com" (unchanged)
 * - "http://example.com" → "http://example.com" (unchanged)
 * - "email@domain.com" → "mailto:email@domain.com"
 * - "mailto:email@domain.com" → "mailto:email@domain.com" (unchanged)
 * - "" → ""
 */
export const normalizeUrl = (url) => {
  // Return empty string for null, undefined, or empty input
  if (!url || url.trim() === '') return '';

  const trimmedUrl = url.trim();

  // If it's already a mailto: link, return as-is
  if (trimmedUrl.startsWith('mailto:')) {
    return trimmedUrl;
  }

  // Check if it's an email address (contains @ but not http/https)
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (emailPattern.test(trimmedUrl)) {
    return `mailto:${trimmedUrl}`;
  }

  // If URL already has a protocol (http:// or https://), return as-is
  if (trimmedUrl.match(/^https?:\/\//i)) {
    return trimmedUrl;
  }

  // If URL starts with www. or contains a dot (domain), add https://
  if (trimmedUrl.match(/^www\./i) || trimmedUrl.includes('.')) {
    return `https://${trimmedUrl}`;
  }

  // Otherwise return as-is (might be incomplete URL being typed or fragment like '#')
  return trimmedUrl;
};

/**
 * Validates if a string is a valid URL
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid URL, false otherwise
 */
export const isValidUrl = (url) => {
  try {
    const normalizedUrl = normalizeUrl(url);
    new URL(normalizedUrl);
    return true;
  } catch {
    return false;
  }
};
